import { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  useMatch,
  useNavigate,
  useParams,
  generatePath,
  NavLink,
} from 'react-router-dom';
import copy from 'copy-to-clipboard';
import {
  IconCopy,
  PermissionEnum,
  Popup,
  ResponseError,
  responseErrorsResolver,
  IconChangeLoClock,
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  ChangeLogs,
  PermissionDenied,
  IconCloseCircle,
  Textarea,
  IconLock,
  IconArrowRight,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  useResetCustomerPasswordMutation,
  useLazyGetCustomersTopUpQuery,
  useLazyGetCustomersChangeLogsQuery,
  useReopenOrganizationMutation,
  useLazyGetAllTopUpVisibilityQuery,
  useLazyGetCustomerCommentsQuery,
  useAddCommentToCustomerMutation,
  useEditCommentInCustomerMutation,
  useDeleteCommentInCustomerMutation,
} from '../../customers/store/services/customers';
import {
  useUpdateIndividualUserBaseInformationMutation,
  useConvertCofToIndividualMutation,
} from '../store/services/individual';
import {
  setConvertedToIndividual,
  setTopUpIds,
} from '../store/slices/general-info';
import OverviewCompanies from './overview-companies';
// import OverviewRecentActivity from './overview-recent-acitivty';
import OverviewStatements from '../../customers/statements/statements';
import GeneralUserInfo from '../../../components/individual/overview/general-user-info';
// import UserLogs from '../../../components/individual/overview/user-logs';
import CustomerComments from '../../../components/partial/comments/comments';
import TopUpOptions from '../../../components/partial/overview-topup/overview-topup';
import SummaryInfo from '../../../components/partial/summary-info/summary-info';
import {
  CustomerStatus,
  CustomerType,
  OrganizationType,
} from '../../customers/types';
import { BaseInfoFormValues } from '../types';
import TopUpPanel from '../../customers/top-up-panel/top-up-panel';

import ROUTES from '../../../routes';

import cn from '../information.module.css';

const IndividualCustomerOverview = (): React.JSX.Element => {
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<BaseInfoFormValues>();
  const [topUpPanelIsOpen, setTopUpPanelIsOpen] = useState(false);
  const [selectedTopUpId, setSelectedTopUpId] = useState('');
  const [token, setToken] = useState('');
  const [resetPasswordPopupVisible, setResetPasswordPopupVisible] =
    useState(false);
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [convertPopupVisible, setConvertPopuVisible] = useState(false);
  const [changeLogsPanelIsOpen, setChangeLogPanelIsOpen] = useState(false);
  const [reopenPopupVisible, setReopenPopupVisible] = useState(false);
  const [reopenPopupState, setReopenPopupState] = useState<
    'warning' | 'comment'
  >('warning');
  const [reopenReason, setReopenReason] = useState('');
  const [reopenError, setReopenError] = useState('');

  const [
    getTopUp,
    { isLoading: topUpOptionsIsLoading, isFetching: topUpOptionsIsFetching },
  ] = useLazyGetCustomersTopUpQuery();
  const [
    getAllTopUpVisibility,
    {
      isLoading: topUpVisibilityIsLoading,
      isFetching: topUpVisibilityIsFetching,
    },
  ] = useLazyGetAllTopUpVisibilityQuery();

  const [resetCustomerPassword, { isLoading: recoveryInProgress }] =
    useResetCustomerPasswordMutation();

  const [
    reopenOrganization,
    { isLoading: reopenInProgress, isSuccess: reopenIsSuccess },
  ] = useReopenOrganizationMutation();

  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetCustomersChangeLogsQuery();

  const [
    getCustomerComments,
    { isLoading: commentsIsLoading, isFetching: commentsIsFetching },
  ] = useLazyGetCustomerCommentsQuery();
  const [
    addCommentToCustomer,
    { isLoading: addCommentInProgress, isSuccess: addCommentIsSuccess },
  ] = useAddCommentToCustomerMutation();
  const [
    editCommentInCustomer,
    { isLoading: editCommentInProgress, isSuccess: editCommentIsSuccess },
  ] = useEditCommentInCustomerMutation();
  const [
    deleteCommentInCustomer,
    { isLoading: deleteCommentInProgress, isSuccess: deleteCommentIsSuccess },
  ] = useDeleteCommentInCustomerMutation();

  const {
    general: {
      baseInformation,
      baseInformationIsLoading,
      totalBalance,
      topUpOptions,
      topUpIsLoading,
      balances,
      topUpOptionsPagination,
      changeLogs,
      changeLogsPagination,
      activeLock,
      topUpVisibility,
      topUpIds,
      customerComments,
      customerCommentsPagination,
    },
    overview: { baseUserInformation },
  } = useAppSelector((state) => state.individualCustomers.info);
  const { cardsEnabled } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const { activePermissions } = useAppSelector((state) => state.root);

  const { users } = useAppSelector((state) => state.administrationDictionaries);

  const { customerId } = useParams();
  const match = useMatch(ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW);
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const [update, { isLoading, isSuccess }] =
    useUpdateIndividualUserBaseInformationMutation();
  const [
    convertToIndividaul,
    { isLoading: convertIsLoading, isSuccess: convertIsSuccess },
  ] = useConvertCofToIndividualMutation();

  const getNextTopUpOptions = (cursor: string) => {
    getTopUp({ cursor });
  };

  const getAllTopUpVisibilityAction = useCallback(
    async (query: string) => {
      await getAllTopUpVisibility({
        organizationId: customerId as string,
        query,
      });
    },
    [customerId],
  );

  const onReopenOrganization = async () => {
    setReopenError('');

    const response = (await reopenOrganization({
      id: customerId as string,
      body: {
        statusChangeReason: reopenReason,
      },
    })) as { error: ResponseError };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setReopenError,
      });
    }
  };

  const onInfoSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const body: any = {
      type: baseInformation?.type as OrganizationType,
      firstName: values.firstName,
      lastName: values.lastName,
      countryId: values.countryId,
      city: values.city,
      address1: values.address1,
      address2: values.address2,
      postalCode: values.postalCode,
      sourceOfFundsIds: values.sourceOfFundsIds,
      productIds: values.productIds,
      nationalityId: values.nationalityId,
    };

    if (values?.clientActivityId) {
      body.clientActivityId = values.clientActivityId;
    }

    if (values.idExpDate) {
      body.idExpDate = values.idExpDate;
    }

    if (values.idType) {
      body.idType = values.idType;
    }

    if (values.idNumber) {
      body.idNumber = values.idNumber;
    }

    if (values.email) {
      body.email = values.email;
    }

    if (values.dateOfBirth) {
      body.dob = values.dateOfBirth;
    }

    if (values.taxNumber) {
      body.taxNumber = values.taxNumber;
    }

    const response = (await update({
      id: customerId as string,
      body,
    })) as {
      error: ResponseError;
    };

    if (response.error) {
      responseErrorsResolver({
        setError,
        errors: response.error,
        setGlobalError,
      });
    }
  });

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=ORGANIZATION&entityId=${customerId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [customerId],
  );

  let link: string;

  if (process.env.CUSTOMERS_HOST) {
    link = process.env.CUSTOMERS_HOST;
  } else {
    link = window.location.host.includes('baseella')
      ? window.location.host.replace('backoffice', 'customers')
      : '';
  }

  const onCopy = () => {
    copy(`${link}/auth/reset-password?token=${token}`);

    setCopyIsSuccess(true);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  const onResetPassword = async () => {
    const response = (await resetCustomerPassword(
      baseInformation?.userId as string,
    )) as {
      data: { data: { token: string } };
    };

    if (response && response.data && response.data.data) {
      setToken(response.data.data.token);
      setResetPasswordPopupVisible(true);
    }
  };

  const onAddCommentToCustomer = async (text: string) => {
    await addCommentToCustomer({
      id: customerId as string,
      body: {
        text,
      },
    });
  };

  const onEditCommentInCustomer = async ({
    text,
    commentId,
  }: {
    text: string;
    commentId: string;
  }) => {
    await editCommentInCustomer({
      id: customerId as string,
      commentId,
      body: {
        text,
      },
    });
  };

  const onDeleteCommentInCustomer = async (commentId: string) => {
    await deleteCommentInCustomer({
      id: customerId as string,
      commentId,
    });
  };

  const getCustomerCommentsAction = async (cursor?: string) => {
    await getCustomerComments({
      id: customerId as string,
      cursor: cursor || '',
    });
  };

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_COMMENTS_LIST,
      )
    ) {
      getCustomerCommentsAction();
    }
  }, []);

  useEffect(() => {
    if (customerId && match) {
      navigate(
        generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW_TRANSACTIONS, {
          customerId,
        }),
      );
    }
  }, [customerId, match]);

  useEffect(() => {
    if (baseUserInformation) {
      reset({
        type: OrganizationType.INDIVIDUAL,
        firstName: baseUserInformation.firstName,
        lastName: baseUserInformation.lastName,
        employmentStatus: baseUserInformation.employmentStatus,
        countryId: baseUserInformation.countryId,
        city: baseUserInformation.city,
        address1: baseUserInformation.address1,
        address2: baseUserInformation.address2,
        postalCode: baseUserInformation.postalCode,
        sourceOfFundsIds: baseUserInformation.sourceOfFunds,
        dateOfBirth: baseUserInformation.dateOfBirth,
        phone: baseUserInformation.phone,
        email: baseUserInformation.email,
        idType: baseInformation?.idType,
        idNumber: baseInformation?.idNumber,
        idExpDate: baseInformation?.idExpDate,
        productIds: baseUserInformation?.products,
        clientActivityId: baseUserInformation?.clientActivityId,
        nationalityId: baseInformation?.nationalityId,
        taxNumber: baseInformation?.taxNumber || '',
      });
    }
  }, [reset, baseUserInformation]);

  useEffect(() => {
    if (convertIsSuccess) {
      dispatch(setConvertedToIndividual(true));
    }
  }, [convertIsSuccess]);

  useEffect(() => {
    let title = baseUserInformation?.fullName
      ? `${baseUserInformation?.fullName}: Overview`
      : 'Overview';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseUserInformation?.fullName]);

  let applicationLink = generatePath(
    ROUTES.CUSTOMERS_NEW_APPLICATION_INDIVIDUAL_INFO,
    { applicationId: baseInformation?.applicationId || '' },
  );

  if (baseInformation?.type === OrganizationType.COMPANY_OFFICIAL) {
    applicationLink = generatePath(
      ROUTES.CUSTOMERS_NEW_APPLICATION_COMPANY_OFFICIAL,
      { applicationId: baseInformation?.applicationId || '' },
    );
  }

  useEffect(() => {
    if (
      changeLogsPanelIsOpen &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [changeLogsPanelIsOpen]);

  useEffect(() => {
    if (customerId && topUpOptions) {
      const nextTopUpIds = ([] as string[]).concat(topUpIds as string[]);

      const topUpIdsValues = topUpOptions?.map((item) => item.id);

      topUpIdsValues?.forEach((item) => {
        if (!nextTopUpIds?.find((id) => item === id)) {
          nextTopUpIds.push(item);
        }
      });

      if (
        topUpIdsValues &&
        topUpIdsValues?.filter((item) => topUpIds?.every((id) => id !== item))
          ?.length
      ) {
        const query = `&pspPartnerId=${topUpIdsValues
          ?.filter((item) => topUpIds?.every((id) => id !== item))
          ?.join('&pspPartnerId=')}`;

        getAllTopUpVisibilityAction(query);
        dispatch(setTopUpIds(nextTopUpIds));
      }
    }
  }, [customerId, topUpOptions, topUpIds]);

  useEffect(() => {
    if (reopenIsSuccess) {
      setReopenError('');
      setReopenPopupState('warning');
      setReopenPopupVisible(false);
    }
  }, [reopenIsSuccess]);

  return (
    <div className={cn.customerContent}>
      <div
        className={`${cn.overviewTitleWrapper} ${
          !baseUserInformation?.fullName ? cn.flexEnd : ''
        }`}
      >
        {baseUserInformation?.fullName && (
          <div className={cn.overviewTitle}>{baseUserInformation.fullName}</div>
        )}
        <div className={cn.overviewTopRight}>
          {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <button
              type="button"
              className={cn.applicationLink}
              onClick={() => setChangeLogPanelIsOpen(true)}
            >
              <IconChangeLoClock />
              Change Log
            </button>
          )}

          {baseInformation?.applicationId && (
            <NavLink
              to={applicationLink}
              className={cn.applicationLink}
              target="_blank"
            >
              Go to application
            </NavLink>
          )}
        </div>
      </div>
      {baseInformation?.status === CustomerStatus.CLOSED && (
        <div className={cn.closedContent}>
          <div className={cn.closeContentLeft}>
            <div className={cn.closeContentLeftTitle}>
              <IconCloseCircle width={24} height={24} />
              Organization is closed
            </div>
            <div className={cn.closeContentLeftDescription}>
              {baseInformation?.statusChangeReason}
            </div>
          </div>
          {activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_OPEN_ACTION,
          ) && (
            <div className={cn.closeContentRight}>
              <button
                type="button"
                className={cn.reopenButton}
                onClick={() => setReopenPopupVisible(true)}
              >
                Reopen
              </button>
            </div>
          )}
        </div>
      )}
      {activeLock && (
        <div
          className={`${cn.lockInfo} ${
            baseInformation?.status === CustomerStatus.CLOSED
              ? cn.withMarginTop
              : ''
          }`}
        >
          <div className={cn.lockInfoLeft}>
            <div className={cn.lockInfoIcon}>
              <IconLock />
            </div>
            <div className={cn.lockInfoText}>Authorization is locked</div>
          </div>
          <div className={cn.lockInfoLinkWrapper}>
            <NavLink
              to={`${generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOCKS, {
                customerId: (customerId as string) || '',
              })}?locked=true`}
              className={cn.lockInfoLink}
            >
              <div className={cn.lockInfoLinkContent}>View locks</div>
              <div className={cn.lockInfoLinkContent}>
                <IconArrowRight />
              </div>
            </NavLink>
          </div>
        </div>
      )}
      <SummaryInfo
        accounts={baseInformation?.accounts?.length as number}
        currencies={totalBalance?.currency as string[]}
        balances={balances}
        balance={totalBalance?.balance as number}
        type={baseUserInformation?.type}
        cardsEnabled={cardsEnabled}
      />
      <GeneralUserInfo
        isLoading={baseInformationIsLoading as boolean}
        generalUserInfo={baseInformation}
        baseUserInformation={baseUserInformation}
        control={control}
        onInfoSubmit={onInfoSubmit}
        requestInProgress={isLoading}
        isSuccess={isSuccess}
        onResetPassword={onResetPassword}
        recoveryInProgress={recoveryInProgress}
        globalError={globalError}
        errors={errors}
        setConvertPopuVisible={setConvertPopuVisible}
        convertToIndividualAvailable={Boolean(
          activePermissions?.includes(
            PermissionEnum.COFF_CUSTOMERS_CONVERT_TO_INDIVIDUAL_ACTION,
          ),
        )}
        status={baseInformation?.status}
      />
      {activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_COMMENTS_LIST,
      ) && (
        <CustomerComments
          comments={customerComments}
          pagination={customerCommentsPagination}
          isLoading={commentsIsLoading}
          isFetching={commentsIsFetching}
          getCommentsList={getCustomerCommentsAction}
          createAvailable={activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_COMMENTS_CREATE_ACTION,
          )}
          editAvailable={activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_COMMENTS_EDIT_ACTION,
          )}
          deleteAvailable={activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_COMMENTS_DELETE_ACTION,
          )}
          onAddCommentToCustomer={onAddCommentToCustomer}
          addCommentInProgress={addCommentInProgress}
          addCommentIsSuccess={addCommentIsSuccess}
          onEditCommentInCustomer={onEditCommentInCustomer}
          editCommentInProgress={editCommentInProgress}
          editCommentIsSuccess={editCommentIsSuccess}
          users={users}
          onDeleteCommentInCustomer={onDeleteCommentInCustomer}
          deleteCommentInProgress={deleteCommentInProgress}
          deleteCommentIsSuccess={deleteCommentIsSuccess}
          changeLogsAvailable={activePermissions?.includes(
            PermissionEnum.AUDIT_LOG_LIST,
          )}
        />
      )}
      {/* <UserLogs /> */}
      <OverviewCompanies
        isLoading={baseInformationIsLoading}
        status={baseInformation?.status}
      />
      {/* <OverviewRecentActivity /> */}
      {baseUserInformation?.type !== OrganizationType.COMPANY_OFFICIAL && (
        <>
          <TopUpOptions
            topUpOptions={topUpOptions}
            isLoading={topUpIsLoading}
            customerType={CustomerType.INDIVIDUAL}
            organizationId={customerId}
            setTopUpPanelIsOpen={setTopUpPanelIsOpen}
            setSelectedTopUpId={setSelectedTopUpId}
            topUpOptionsPagination={topUpOptionsPagination}
            getNextTopUpOptions={getNextTopUpOptions}
            topUpOptionsIsLoading={
              topUpOptionsIsLoading || topUpOptionsIsFetching
            }
            status={baseInformation?.status}
            topUpVisibility={topUpVisibility}
            topUpVisibilityIsLoading={
              topUpVisibilityIsLoading || topUpVisibilityIsFetching
            }
          />
          <TopUpPanel
            topUpPanelIsOpen={topUpPanelIsOpen}
            setTopUpPanelIsOpen={setTopUpPanelIsOpen}
            selectedTopUpId={selectedTopUpId}
            setSelectedTopUpId={setSelectedTopUpId}
            type={CustomerType.INDIVIDUAL}
            userAccounts={baseInformation?.accounts?.map((item) => ({
              accountId: item.id,
              account: { fullNumber: item.fullNumber },
              description: item.description,
            }))}
          />
        </>
      )}
      {activePermissions?.includes(
        PermissionEnum.CUSTOMERS_STATEMENTS_LIST,
      ) && (
        <OverviewStatements
          matchPath={ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW_TRANSACTIONS}
          cardsRoute={ROUTES.CUSTOMERS_INDIVIDUAL_CARDS}
          routes={{
            transationRoute: ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW_TRANSACTIONS,
            cardsRoute: ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW_CARDS,
          }}
          accounts={
            baseInformation?.id === customerId
              ? baseInformation?.accounts
              : undefined
          }
          cards={baseInformation?.cards as string[]}
          cardsEnabled={cardsEnabled}
          balances={balances}
          type={baseUserInformation?.type}
        />
      )}
      <Popup
        title="Are you sure you want to convert cof to an individual customer?"
        onCancelClick={() => setConvertPopuVisible(false)}
        okButtonText={convertIsLoading ? 'Loading...' : 'Yes'}
        cancelButtonText="No"
        isVisible={convertPopupVisible}
        isLoading={convertIsLoading}
        onOkClick={() => convertToIndividaul(customerId as string)}
      />
      <Popup
        title={
          reopenPopupState === 'warning'
            ? 'Reopen organization'
            : 'State the reason for reopening'
        }
        description={
          reopenPopupState === 'warning'
            ? 'The organization will become available again'
            : ''
        }
        onCancelClick={() => {
          setReopenPopupVisible(false);
          setReopenPopupState('warning');
          setReopenError('');
        }}
        okButtonText={reopenInProgress ? 'Loading...' : 'Reopen organization'}
        cancelButtonText="Cancel"
        isVisible={reopenPopupVisible}
        isLoading={reopenInProgress}
        okButtonDisabled={
          Boolean(reopenPopupState === 'comment' && !reopenReason) ||
          reopenInProgress
        }
        onOkClick={() => {
          if (reopenPopupState === 'warning') {
            setReopenPopupState('comment');
          } else if (reopenPopupState === 'comment') {
            onReopenOrganization();
          }
        }}
      >
        <div>
          {reopenPopupState === 'comment' && (
            <>
              {reopenError && (
                <div className={cn.reopenError}>{reopenError}</div>
              )}
              <Textarea
                name="reopenReason"
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="Reason"
                labelClassName={cn.reopenReason}
              />
            </>
          )}
        </div>
      </Popup>
      <Popup
        title="Reset customer password"
        description="Send this link to the user for password recovery"
        onCancelClick={() => setResetPasswordPopupVisible(false)}
        isVisible={resetPasswordPopupVisible}
      >
        <div className={cn.resetPasswordLink}>
          <div
            className={`${cn.copySuccess} ${copyIsSuccess ? cn.visible : ''}`}
          >
            Copied!
          </div>
          <span>{`${link}/auth/reset-password?token=${token}`}</span>
          <button
            type="button"
            className={cn.copyButton}
            onClick={onCopy}
            aria-label="Copy link button"
          >
            <IconCopy />
          </button>
        </div>
      </Popup>
      <SidePanel
        isOpen={changeLogsPanelIsOpen}
        onClose={() => {
          setChangeLogPanelIsOpen(false);
        }}
        title="Customer change logs"
        sidepanelExpandedKey="customers-change-logs"
        type={SidePanelType.Accounts}
        footer={
          <div>
            <ButtonPseudo
              text="Close"
              onClick={() => setChangeLogPanelIsOpen(false)}
              bold
            />
          </div>
        }
        closeOnClickOutside
      >
        <>
          {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <ChangeLogs
              entityType="ORGANIZATION"
              entityId={customerId as string}
              changeLogsIsLoading={changeLogsIsLoading}
              changeLogs={changeLogs}
              activePermission={activePermissions}
              users={users}
              pagination={changeLogsPagination}
              changeLogsIsFetching={changeLogsIsFetching}
              getChangeLogs={getChangeLogsAction}
            />
          )}
          {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <PermissionDenied />
          )}
        </>
      </SidePanel>
    </div>
  );
};

export default IndividualCustomerOverview;
